import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Projector } from "./Projector";
import { glowTex, clamp01, ease } from "./geo";
import { fmt } from "./ui";

export function useDrag(def) {
  const s = useRef({ dragging: false, rx: def[0], ry: def[1], lx: 0, ly: 0 });
  const handlers = {
    onPointerDown: (e) => {
      Object.assign(s.current, { dragging: true, lx: e.clientX, ly: e.clientY });
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e) => {
      const c = s.current;
      if (!c.dragging) return;
      c.ry += (e.clientX - c.lx) * 0.008;
      c.rx = Math.max(-0.5, Math.min(1.0, c.rx + (e.clientY - c.ly) * 0.005));
      c.lx = e.clientX;
      c.ly = e.clientY;
    },
    onPointerUp: () => { s.current.dragging = false; },
    onPointerCancel: () => { s.current.dragging = false; },
    onClick: (e) => e.stopPropagation(),
  };
  return [s, handlers];
}

export function useSpring(group, drag, def) {
  useFrame((st) => {
    const c = drag.current;
    if (!c.dragging) {
      c.ry += (def[1] + Math.sin(st.clock.elapsedTime * 0.35) * 0.1 - c.ry) * 0.035;
      c.rx += (def[0] - c.rx) * 0.035;
    }
    if (group.current) group.current.rotation.set(c.rx, c.ry, 0);
  });
}

function BarsScene({ data, max, height, spacing, width, targets, vals, drag, def, delay, stagger, dur, dec }) {
  const group = useRef();
  const bars = useRef([]);
  const t0 = useRef(null);
  const geo = useMemo(() => new THREE.BoxGeometry(width, 1, width).translate(0, 0.5, 0), [width]);
  const x0 = -((data.length - 1) * spacing) / 2;
  useSpring(group, drag, def);
  useFrame((s) => {
    if (t0.current === null) t0.current = s.clock.elapsedTime;
    const ms = (s.clock.elapsedTime - t0.current) * 1000;
    data.forEach((d, i) => {
      let v;
      if (d.from != null) {
        const p1 = ease(clamp01((ms - delay) / 900));
        const p2 = ease(clamp01((ms - d.at) / dur));
        v = d.from * p1 + (d.value - d.from) * p2;
      } else v = d.value * ease(clamp01((ms - delay - i * stagger) / dur));
      const h = Math.max(0.002, (v / max) * height);
      if (bars.current[i]) bars.current[i].scale.y = h;
      if (targets.current[i]) targets.current[i].position.y = h + 0.12;
      if (vals.current[i]) vals.current[i].textContent = fmt(v, dec);
    });
  });
  return (
    <group ref={group} position={[0, -1.6, 0]}>
      {data.map((d, i) => (
        <group key={d.label} position={[x0 + i * spacing, 0, 0]}>
          <mesh ref={(el) => (bars.current[i] = el)} geometry={geo}>
            <meshStandardMaterial color={d.color} emissive={d.color} emissiveIntensity={0.55} roughness={0.3} metalness={0.35} transparent opacity={0.93} />
          </mesh>
          <object3D ref={(el) => (targets.current[i] = el)} />
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.003, 0]}>
            <planeGeometry args={[width * 3, width * 3]} />
            <meshBasicMaterial map={glowTex()} color={d.color} transparent blending={THREE.AdditiveBlending} depthWrite={false} opacity={0.8} />
          </mesh>
        </group>
      ))}
      <gridHelper args={[data.length * spacing + 4, 28, "#5a3a1c", "#241a12"]} />
    </group>
  );
}

export function Bars3D({ data, max, height = 4, spacing = 1.6, width = 0.8, def = [0.32, -0.42], delay = 400, stagger = 220, dur = 1600, dec = 1, testid, cam = [0, 2.2, 9.5] }) {
  const targets = useRef([]);
  const labels = useRef([]);
  const vals = useRef([]);
  const [drag, handlers] = useDrag(def);
  return (
    <div className="chart3d" data-testid={testid} data-nonav {...handlers}>
      <Canvas camera={{ position: cam, fov: 36 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }} resize={{ offsetSize: true }}>
        <fog attach="fog" args={["#050506", 9, 22]} />
        <ambientLight intensity={0.35} />
        <directionalLight position={[4, 7, 5]} intensity={1.8} color="#ffd2a0" />
        <pointLight position={[-5, 3, -2]} intensity={40} color="#6cc5ff" />
        <BarsScene {...{ data, max, height, spacing, width, targets, vals, drag, def, delay, stagger, dur, dec }} />
        <Projector targets={targets} labels={labels} />
      </Canvas>
      <div className="labels3d">
        {data.map((d, i) => (
          <div key={d.label} className="bar-label" ref={(el) => (labels.current[i] = el)}>
            <div>
              <span className="bar-val" ref={(el) => (vals.current[i] = el)} data-testid={`${testid}-val-${i}`}>0</span>
              <span className="bar-name">{d.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
